import {
  hashKey,
  MutationCache,
  QueryClient,
  type NoInfer,
  type QueryKey,
  type SetDataOptions,
  type Updater,
} from '@tanstack/react-query';

import { billingRefusalOf, openBillingGate } from '@/lib/billing-gate';
import { getCurrentOrganizationId } from '@/lib/current-organization';
import { onLanguageTransition } from '@/lib/i18n/i18n';

// Auth keys are the source of the org id, not tenant data — hashing 'me' by it would self-bust.
const isTenantScoped = (queryKey: QueryKey): boolean => queryKey[0] !== 'auth';

// Namespaces every entry by the active org, so the next tenant can never read the previous one's.
const hashByOrganization = (queryKey: QueryKey): string =>
  isTenantScoped(queryKey)
    ? hashKey([getCurrentOrganizationId(), ...queryKey])
    : hashKey(queryKey);

// Drops tenant-scoped writes made while no tenant is established — what a cleared
// identity's still-in-flight mutation rollback does. The entry would be unreadable by
// the next user regardless, so this only stops the dead session repainting it.
class IdentityScopedQueryClient extends QueryClient {
  override setQueryData<TData>(
    queryKey: QueryKey,
    updater: Updater<NoInfer<TData> | undefined, NoInfer<TData> | undefined>,
    options?: SetDataOptions,
  ): TData | undefined {
    if (getCurrentOrganizationId() === null && isTenantScoped(queryKey)) {
      return undefined;
    }
    return super.setQueryData(queryKey, updater, options);
  }
}

// Every mutation funnels its rejection through here, opening the gate before its own onError.
export const queryClient = new IdentityScopedQueryClient({
  mutationCache: new MutationCache({
    onError: (error) => {
      openBillingGate(billingRefusalOf(error));
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
      queryKeyHashFn: hashByOrganization,
    },
  },
});

// Backend-authored copy (recommendations, insights, notifications) is cached in the old language.
onLanguageTransition(() => void queryClient.invalidateQueries());
